import { useNavigation } from "@react-navigation/native";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { ONBOARDING_NAVIGATION_ERROR } from "../../constants/onboardingMessages";
import ScreenNames from "../../constants/routeNames";
import { getNextScreen } from "../../navigation/simpleOnboardingFlow";
import type { OnboardingCookingStylesEnum } from "../../services/backendTypes";
import { selectCookingStyles, setCookingStyles } from "../../slices/simpleOnboardingSlice";
import SelectionScreen, { type Option } from "../New_DIY_form/SelectionScreen";

const validationSchema = Yup.object().shape({
  cookingStyles: Yup.array()
    .of(Yup.string().oneOf(["QUICK", "EASY", "COMFORT", "MEAL_PREP", "FAMILY_FRIENDLY"]))
    .min(1, "Please select at least one cooking style")
    .max(2, "You can select a maximum of 2 cooking styles")
    .required("Please select your cooking preferences"),
});

const SimpleOnboardingCookingStyleScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const selectedCookingStyles = useSelector(selectCookingStyles);

  const options: Option[] = [
    {
      value: "QUICK" as OnboardingCookingStylesEnum,
      label: t("simple_onboarding.cooking_style.QUICK"),
      description: "",
      icon: "Timer",
      iconType: "lucide",
    },
    {
      value: "EASY" as OnboardingCookingStylesEnum,
      label: t("simple_onboarding.cooking_style.EASY"),
      description: "",
      icon: "CircleCheck",
      iconType: "lucide",
    },
    {
      value: "COMFORT" as OnboardingCookingStylesEnum,
      label: t("simple_onboarding.cooking_style.COMFORT"),
      description: "",
      icon: "Pizza",
      iconType: "lucide",
    },
    {
      value: "MEAL_PREP" as OnboardingCookingStylesEnum,
      label: t("simple_onboarding.cooking_style.MEAL_PREP"),
      description: "",
      icon: "Refrigerator",
      iconType: "lucide",
    },
    {
      value: "FAMILY_FRIENDLY" as OnboardingCookingStylesEnum,
      label: t("simple_onboarding.cooking_style.FAMILY_FRIENDLY"),
      description: "",
      icon: "Baby",
      iconType: "lucide",
    },
  ];

  return (
    <Formik
      initialValues={{ cookingStyles: selectedCookingStyles || [] }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        dispatch(setCookingStyles(values.cookingStyles));
        const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_2_CookingStyle);
        if (nextScreen) {
          navigation.navigate(nextScreen as never);
        } else {
          alert(ONBOARDING_NAVIGATION_ERROR);
        }
      }}
    >
      {({ values, setFieldValue, errors, handleSubmit }) => (
        <SelectionScreen
          title={t("simple_onboarding.cooking_style.title")}
          subtitle={t("simple_onboarding.cooking_style.subtitle")}
          options={options}
          multipleSelection={true}
          selectedOptions={values.cookingStyles}
          setSelectedOptions={(cookingStyleOptions) => {
            if (cookingStyleOptions.length <= 2) {
              setFieldValue("cookingStyles", cookingStyleOptions);
            }
          }}
          onPressNext={handleSubmit}
          onPressBack={() => navigation.goBack()}
          nextButtonDisabled={values.cookingStyles.length === 0}
          errorMessage={errors.cookingStyles}
          nextButtonTestIdPostfix="cooking-style"
        />
      )}
    </Formik>
  );
};

export default SimpleOnboardingCookingStyleScreen;
